
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, MenuItem, Menu, TextField, Typography, Chip } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import PrintIcon from '@mui/icons-material/Print';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TemplatePrintButton from '../../../../print/TemplatePrintButton';
import PrintPreviewDialog from '../../../../print/PrintPreviewDialog';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { addYears } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDialogModal from "../../../../../hooks/useDialogModal";
import { calculateItemTotal, formatAmountForDisplayWithPreferences, getCurrentUTCDate } from "../../../../../utils";
import validLookup from "../../../../../utils/validLookup";
import { SelectLocation } from "../../../../common";
import AppDialogTitle from "../../../../ui/appDialogTitle";
import InvoiceButtonComponent, { ButtonType } from "./invoiceButtonComponents";
import TreatmentSelectDialog from "../../../../admin/ValidData/validTreatment/treatmentSelectDialog";
import AccessorySelectDialog from "../../../../admin/inventory/accessories/accessorySelectDialog";
import InvoiceItemsTable from "./invoiceItemsTable";
import PaymentCollector from "./paymentCollector";
import DiscountPromotion from "./discountAndPromotion";
import InsuranceClaimCollector from "./insuranceClaimCollector";
import RefundCollector from "./refundCollector";
import CreditMemoCollector from "./creditMemoCollector";
import { useQuery } from "react-query";
import { ValidDataService } from "../../../../../service/validDataService";
import InvoiceStatusSelect from "./invoiceStatusSelect";
import { InvoiceService } from "../../../../../service/invoiceService";
import CreditMemoService from "../../../../../service/creditMemoService";
import { isLocalDev } from "../../../../../utils/environment";
import { toast } from "react-toastify";
import CustomerInfobar from "../../../customerInfobar";
import ConfirmationDialog from "../../../../ui/confirmationDialog";
import NewGlassesDialog from "./newGlassesDialog";
import ManualGlassesDialog from "./ManualGlassesDialog";
import ManualLensDialog from "./ManualLensDialog";
import ManualContactLensDialog from "./ManualContactLensDialog";
import ContactLensConfigurationDialog from "./ContactLensConfigurationDialog";
import ServiceSelectDialog from "../../../../admin/ValidData/validServices/serviceSelectDialog";
import TransferInvoiceDialog from "./TransferInvoiceDialog";
import CreateReturnInvoiceDialog from "./CreateReturnInvoiceDialog";
import { useInvoiceSMS } from '../../../../../hooks/useInvoiceSMS';
import { v4 as uuidv4 } from 'uuid'
import useInvoiceCalculations from "../../../../../hooks/useInvoiceCalculations";
import useInvoiceState from "../../../../../hooks/useInvoiceState";
import FrameSelectDialog from "../../../../admin/inventory/frames/frameSelectDialog";
// LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow
import { usePrint } from "../../../../../hooks/usePrint";
import AddTaskDialog from "../../../../task/AddTaskDialog";
import Can from "components/auth/Can";
import SendSmsDialog from "../../../../appointments/SendSmsDialog";
import SendInvoiceEmailDialog from './SendInvoiceEmailDialog';
import {
    buildGlassesInvoiceItems,
    buildUpdatedGlassesInvoiceItems,
    mapGroupItemsToGlassesData,
} from "./glassesOrderMapper";
import { useCan } from "hooks/useCan";
import { usePreferences } from "hooks/usePreferences";
import { formatDateWithPreferences } from "utils/dateFormatUtils";
import { toLocalDateInputValue } from "components/appointments/utils";
import FrameDispositionDialog from "./FrameDispositionDialog";
import { FRAME_HANDLING_PATIENT_OWN } from "../invoice";

const isDateOnly = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
const VOID_STATUSES = new Set(['void', 'voided']);
const INITIAL_VOID_WORKFLOW_STATE = {
    inProgress: false,
    completed: false,
    decisions: [],
    requireVoidDispositionDecisions: false,
    stagedStatus: null,
    itemSignature: '',
};

const toInvoiceDateInput = (value, timezone) => {
    if (!value) return toLocalDateInputValue(new Date(), timezone || 'UTC');
    if (isDateOnly(value)) return value;
    return toLocalDateInputValue(value, timezone || 'UTC');
};

export default function EditInvoiceDialog({ onClose, open, invoiceId, onRefresh, onOpenReturnInvoice }) {
    const [hydratedInvoiceData, setHydratedInvoiceData] = useState(null);
    const [isHydrating, setIsHydrating] = useState(false);
    const [hydrationError, setHydrationError] = useState('');
    const safeCustomer = useMemo(() => hydratedInvoiceData?.customer || {}, [hydratedInvoiceData?.customer]);
    const customerContext = useMemo(() => ({ customer: safeCustomer }), [safeCustomer]);
    const appliedHydrationInvoiceIdRef = useRef(null);

    const can = useCan();
    const { preferences } = usePreferences();
    const effectiveTimezone = hydratedInvoiceData?.location?.timezone || preferences?.timezone || 'UTC';

    const { data: invoiceStatusType, isLoading: invoivceStatusTypeLoading } = useQuery('invoiceStatusTypes', ValidDataService.getInvoiceStatusTypes)

    const hydrateInvoiceById = useCallback(async (id) => {
        if (!id) {
            setHydrationError('Missing invoice id. Unable to load invoice.');
            setHydratedInvoiceData(null);
            return null;
        }

        setIsHydrating(true);
        setHydrationError('');

        try {
            const response = await InvoiceService.getById(id);
            const fullInvoice = response?.data || response;

            if (!fullInvoice?.id) {
                throw new Error('Invoice was not found.');
            }

            appliedHydrationInvoiceIdRef.current = null;
            setHydratedInvoiceData(fullInvoice);
            return fullInvoice;
        } catch (error) {
            const message = error?.message || 'Failed to load invoice details.';
            setHydrationError(message);
            setHydratedInvoiceData(null);
            if (isLocalDev) {
                console.error('Failed to hydrate invoice by id:', error);
            }
            return null;
        } finally {
            setIsHydrating(false);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setHydratedInvoiceData(null);
            setHydrationError('');
            setIsHydrating(false);
            appliedHydrationInvoiceIdRef.current = null;
            return;
        }

        hydrateInvoiceById(invoiceId);
    }, [hydrateInvoiceById, invoiceId, open]);

    const {
        invoiceState: state,
        actions
    } = useInvoiceState({
        id: hydratedInvoiceData?.id || invoiceId || null,
        invoiceNumber: hydratedInvoiceData?.invoiceNumber,
        selectService: false,
        selectTreatment: false,
        selectAccessory: false,
        selectFrame: false,
        selectLens: false,
        selectGlasses: false,
        selectManualGlasses: false,
        selectManualLens: false,
        selectManualContactLens: false,
        selectContacts: false,
        selectConfiguredContactLens: false,
        editGlasses: false,
        editManualGlasses: false,
        manualFrameToEdit: null,
        editManualLens: false,
        manualLensToEdit: null,
        editManualContactLens: false,
        manualContactLensToEdit: null,
        editContacts: false,
        editFrame: false,
        editingFrameItem: null,        
        labOrderDialog: false,
        glassesToEdit: null,
        contactsToEdit: null,
        inProgress: false,
        location: hydratedInvoiceData?.location || null,
        notes: hydratedInvoiceData?.notes || '',
        tray: hydratedInvoiceData?.tray || '',
        associate: hydratedInvoiceData?.associate || '',
        invoiceItems: [],
        glassesOrdersForLab: [],
        payments: [],
        discounts: [],
        claims: [],
        refunds: [],
        creditMemos: [],
        returnInvoices: hydratedInvoiceData?.returnInvoices || [],
        status: hydratedInvoiceData?.status || 'open',
        dispensedDate: hydratedInvoiceData?.dispensedDate || null,
        dispensedBy: hydratedInvoiceData?.dispensedBy || '',
        invoiceDate: toInvoiceDateInput(hydratedInvoiceData?.invoiceDate, effectiveTimezone),
        endDate: addYears(Date.now(), 1),
        tabIndex: 0,
        errors: {},
        showTaskDialog: false,
    });

    useEffect(() => {
        if (!open || !hydratedInvoiceData?.id) {
            return;
        }

        if (appliedHydrationInvoiceIdRef.current === hydratedInvoiceData.id) {
            return;
        }

        appliedHydrationInvoiceIdRef.current = hydratedInvoiceData.id;

        actions.setInvoiceMeta({
            id: hydratedInvoiceData.id,
            invoiceNumber: hydratedInvoiceData.invoiceNumber || null,
            location: hydratedInvoiceData.location || null,
            notes: hydratedInvoiceData.notes || '',
            tray: hydratedInvoiceData.tray || '',
            associate: hydratedInvoiceData.associate || '',
            returnInvoices: hydratedInvoiceData.returnInvoices || [],
            status: hydratedInvoiceData.status || 'open',
            dispensedDate: hydratedInvoiceData.dispensedDate || null,
            dispensedBy: hydratedInvoiceData.dispensedBy || '',
            invoiceDate: toInvoiceDateInput(hydratedInvoiceData.invoiceDate, effectiveTimezone)
        });
        actions.setItems(hydratedInvoiceData.items || []);
        actions.setPayments(hydratedInvoiceData.payments || []);
        actions.setDiscounts(hydratedInvoiceData.discounts || []);
        actions.setClaims(hydratedInvoiceData.insuranceClaims || []);
        actions.setRefunds(hydratedInvoiceData.refunds || []);
        actions.setCreditMemos(hydratedInvoiceData.creditMemos || []);
    }, [actions, effectiveTimezone, hydratedInvoiceData, open]);

    useInvoiceSMS();
    const [sendSmsDialog, setSendSmsDialog] = useState({ open: false, invoice: null });
    const [sendEmailDialog, setSendEmailDialog] = useState({ open: false, invoice: null });
    const [messagingMenuAnchorEl, setMessagingMenuAnchorEl] = useState(null);
    const [invoiceActionsMenuAnchorEl, setInvoiceActionsMenuAnchorEl] = useState(null);
    const [showCreateReturnInvoiceDialog, setShowCreateReturnInvoiceDialog] = useState(false);
    const [frameDispositionDialog, setFrameDispositionDialog] = useState({
        open: false,
        previews: [],
        title: 'Frame Handling Required',
        confirmLabel: 'Continue',
        inProgress: false,
    });
    const [voidWorkflow, setVoidWorkflow] = useState(INITIAL_VOID_WORKFLOW_STATE);
    const frameDispositionResolverRef = useRef(null);

    const { printPDF, previewUrl, closePreview, isGenerating: isPrinting, cleanup } = usePrint();

    const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
    const invoicePrintRef = useRef();
    const superbillPrintRef = useRef();
    const receiptPrintRef = useRef();

    useEffect(() => {
        if (!open) {
            cleanup();
        }
    }, [open, cleanup]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => () => cleanup(), []);


    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const { lineItemsSubtotal, totalDiscount, totalDue, balanceDue, totalItemTaxAmount, totalOverallDiscountAmount,
        taxOnDiscountedAmount,
        totalCustomerPaid,
        totalInsuranceApproved,
        maxRefundAmount } = useInvoiceCalculations(state);
    const hasNonZeroDiscount = Math.abs(totalDiscount || 0) > 0.000001;
    const linkedReturnInvoice = useMemo(() => {
        if (!Array.isArray(state.returnInvoices) || state.returnInvoices.length === 0) {
            return null;
        }

        return state.returnInvoices[0];
    }, [state.returnInvoices]);

    const wasClosedInitially = useMemo(() => {
        // we should allow editing of closed invoice e.g. what if customer comes back for a refund
        const persistedLockedStatus = hydratedInvoiceData?.status === 'refund';
        // const currentlyDispensed = Boolean(state.dispensedDate || state.dispensedBy);
        return persistedLockedStatus; // || currentlyDispensed;
    }, [hydratedInvoiceData?.status]);
    const isQuote = useMemo(() => state.status === 'quote', [state.status]);
    const canSendText = useMemo(() => can.action('invoice', 'send-text'), [can]);
    const canSendEmail = useMemo(() => can.action('invoice', 'send-email'), [can]);
    const hasMessagingOptions = canSendText || canSendEmail;
    const canSendTextNow = canSendText && !wasClosedInitially && Boolean(safeCustomer.mobile) && Boolean(state.invoiceNumber);
    const canSendEmailNow = canSendEmail && !wasClosedInitially && Boolean(safeCustomer.email) && Boolean(state.invoiceNumber);
    const canDispenseInvoice = useMemo(() => {
        if (!state.id || state.invoiceItems.length === 0 || isQuote || state.inProgress) {
            return false;
        }
        if (balanceDue > 0 && !preferences.allowCloseWithBalance) {
            return false;
        }
        return !['cancelled', 'void', 'refund', 'transferred', 'closed'].includes(state.status);
    }, [balanceDue, isQuote, preferences.allowCloseWithBalance, state.id, state.inProgress, state.invoiceItems.length, state.status]);
    const hasManualFrameItems = useMemo(() => {
        return (state.invoiceItems || []).some((item) => {
            const itemType = String(item?.type || '').toLowerCase();
            const frameMode = item?.frameHandling || item?.frameDetails?.mode || item?.frame?.frameHandling || item?.frame?.frameDetails?.mode;
            return itemType === 'frame' && frameMode === FRAME_HANDLING_PATIENT_OWN;
        });
    }, [state.invoiceItems]);
    const canTransferInvoice = useMemo(() => {
        if (wasClosedInitially || state.inProgress) return false;
        if (state.status === 'transferred') return false;
        if (hasManualFrameItems) return false;
        return !['cancelled', 'void', 'refund'].includes(state.status);
    }, [hasManualFrameItems, state.inProgress, state.status, wasClosedInitially]);
    const canCreateReturnInvoice = useMemo(() => {
        if (!state.id || state.invoiceItems.length === 0 || isQuote || state.inProgress) {
            return false;
        }
        return !['cancelled', 'void', 'refund', 'transferred', 'closed'].includes(state.status);
    }, [isQuote, state.id, state.inProgress, state.invoiceItems.length, state.status]);
    const hasInvoiceLifecycleActions = isQuote || canTransferInvoice || canDispenseInvoice || canCreateReturnInvoice;


    // validate before saving
    const validate = () => {
        const newErrors = {};
        if (!state.location) newErrors.location = 'Location is required';
        if (!state.associate) newErrors.associate = 'Associate is required';
        if (!state.tray) newErrors.tray = 'Tray is required';

        actions.setInvoiceMeta({ type: 'SET_ERRORS', errors: newErrors });
        return Object.keys(newErrors).length === 0;
    }

    const handleSendTextNotification = () => {
        setSendSmsDialog({ 
            open: true, 
            invoice: {
                id: state.id,
                invoiceNumber: state.invoiceNumber
            }
        });
    };

    const handleSendEmailNotification = () => {
        setSendEmailDialog({
            open: true,
            invoice: {
                id: state.id,
                invoiceNumber: state.invoiceNumber
            }
        });
    };

    const handleOpenMessagingMenu = (event) => {
        setMessagingMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMessagingMenu = () => {
        setMessagingMenuAnchorEl(null);
    };

    const handleOpenInvoiceActionsMenu = (event) => {
        setInvoiceActionsMenuAnchorEl(event.currentTarget);
    };

    const handleCloseInvoiceActionsMenu = () => {
        setInvoiceActionsMenuAnchorEl(null);
    };

    const handlePrintInvoice = async (templateId) => {
        if (!state.invoiceNumber) {
            toast.error('Please save the invoice before printing');
            return;
        }

        if (!state.id) {
            toast.error('Invoice not ready for printing');
            return;
        }

        try {
            await printPDF(() => InvoiceService.print(state.id, templateId));
        } catch (error) {
            if (isLocalDev) {
                console.error('Error printing invoice:', error);
            }
            toast.error('Failed to print invoice');
        }
    };

    const handlePrintSuperbill = async (templateId) => {
        try {
            await printPDF(() => InvoiceService.printSuperbill(state.id, templateId));
        } catch (error) {
            if (isLocalDev) {
                console.error('Error printing superbill:', error);
            }
            toast.error('Failed to print superbill');
        }
    };

    const handlePrintReceipt = async (templateId) => {
        try {
            await printPDF(() => InvoiceService.printReceipt(state.id, templateId));
        } catch (error) {
            if (isLocalDev) {
                console.error('Error printing receipt:', error);
            }
            toast.error('Failed to print receipt');
        }
    };

    const handleConvertToInvoice = async () => {
        if (state.status !== 'quote') {
            toast.error('Only quotes can be converted to invoices');
            return;
        }

        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.convertQuoteToInvoice(state.id);
            actions.setInvoiceMeta({ inProgress: false });
            const { code, message, data } = response;

            if (code !== 0) {
                return;
            }

            // Update the state with the new invoice details
            actions.setInvoiceMeta({
                invoiceNumber: data.invoiceNumber,
                status: data.status
            });
            toast.success(message);

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error converting quote to invoice:', error);
            }
        }
    };

    const handleTransferSuccess = async (transferredInvoice) => {
        if (!transferredInvoice?.id) {
            return;
        }

        const hydrated = await hydrateInvoiceById(transferredInvoice.id);
        actions.setInvoiceMeta({ showTransferDialog: false });

        if (hydrated && onRefresh) {
            onRefresh();
        }
    }

    const handleReturnInvoiceSuccess = async (returnInvoice) => {
        if (!returnInvoice?.id) {
            return;
        }

        const hydrated = await hydrateInvoiceById(returnInvoice.id);

        if (onOpenReturnInvoice && hydrated?.id) {
            onOpenReturnInvoice(hydrated.id);
            onClose();
            return;
        }

        if (hydrated && onRefresh) {
            onRefresh();
        }
    }

    const handleTransferInvoice = async () => {
        if (hasManualFrameItems) {
            toast.error('Transfer is not supported for invoices with manual frame entries');
            return;
        }

        if (state.status === 'transferred') {
            toast.error('This invoice has already been transferred');
            return;
        }

        if (['cancelled', 'voided', 'refunded'].includes(state.status)) {
            toast.error(`Cannot transfer invoice with status: ${state.status}`);
            return;
        }

        // Show the transfer options dialog
        actions.setInvoiceMeta({ showTransferDialog: true });
    };

    const handleCreateReturnInvoice = async (returnPayload) => {
        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.createReturnInvoice(state.id, returnPayload);
            actions.setInvoiceMeta({ inProgress: false });

            const { code, message, data } = response;
            if (code !== 0) {
                toast.error(message || 'Failed to create return invoice');
                return false;
            }

            toast.success(message || 'Return invoice created successfully');
            await handleReturnInvoiceSuccess(data);

            if (onRefresh) {
                onRefresh();
            }

            return true;
        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error creating return invoice:', error);
            }
            toast.error('Error creating return invoice');
            return false;
        }
    };

    const handleOpenLinkedReturnInvoice = () => {
        if (!linkedReturnInvoice?.id || !onOpenReturnInvoice) {
            return;
        }

        onOpenReturnInvoice(linkedReturnInvoice.id);
    };

    const handleAddTask = () => {
        actions.setInvoiceMeta({ showTaskDialog: true });
    };

    const buildVoidItemSignature = useCallback((items = []) => {
        return JSON.stringify(
            items
                .map((item) => ({
                    id: item?.id || null,
                    tempId: item?.tempId || null,
                    itemId: item?.itemId || null,
                    type: item?.type || null,
                    frameHandling: item?.frameHandling || null,
                    quantity: Number(item?.quantity || 0),
                    markedForDeletion: Boolean(item?._markedForDeletion),
                }))
                .sort((a, b) => {
                    const aKey = String(a.id || a.tempId || '');
                    const bKey = String(b.id || b.tempId || '');
                    return aKey.localeCompare(bKey);
                })
        );
    }, []);

    const resetVoidWorkflow = useCallback(() => {
        setVoidWorkflow(INITIAL_VOID_WORKFLOW_STATE);
    }, []);

    const requestConfirmation = useCallback((dialogOptions) => {
        return new Promise((resolve) => {
            showConfirmationDialog((action) => {
                const confirmed = action === validLookup.confirmationDialogAction.ok;
                closeConfirmationDialog();
                resolve(confirmed);
            }, dialogOptions);
        });
    }, [closeConfirmationDialog, showConfirmationDialog]);

    const openFrameDispositionDialog = useCallback((previews, dialogOptions = {}) => {
        return new Promise((resolve) => {
            frameDispositionResolverRef.current = resolve;
            setFrameDispositionDialog({
                open: true,
                previews,
                title: dialogOptions.title || 'Frame Handling Required',
                confirmLabel: dialogOptions.confirmLabel || 'Continue',
                inProgress: false,
            });
        });
    }, []);

    const closeFrameDispositionDialog = () => {
        setFrameDispositionDialog((prev) => ({ ...prev, open: false, inProgress: false }));
        if (frameDispositionResolverRef.current) {
            frameDispositionResolverRef.current(null);
            frameDispositionResolverRef.current = null;
        }
    };

    const confirmFrameDispositionDialog = (decisionsByInvoice) => {
        setFrameDispositionDialog((prev) => ({ ...prev, open: false, inProgress: false }));
        if (frameDispositionResolverRef.current) {
            frameDispositionResolverRef.current(decisionsByInvoice || null);
            frameDispositionResolverRef.current = null;
        }
    };

    const collectVoidDispositionDecisions = useCallback(async () => {
        if (!state.id) {
            toast.error('Invoice must be saved before it can be voided.');
            return null;
        }

        const previewResponse = await InvoiceService.getDispositionPreview(state.id);
        const previewCode = previewResponse?.code;
        if (typeof previewCode === 'number' && previewCode !== 0) {
            toast.error(previewResponse?.message || 'Unable to load frame disposition details.');
            return null;
        }

        const preview = previewResponse?.data || null;
        if (!preview) {
            toast.error('Unable to load frame disposition details.');
            return null;
        }

        const requiresDecision = (preview.decisions || []).some((item) => item?.requiresDecision);
        if (!requiresDecision) {
            return {
                decisions: [],
                requireVoidDispositionDecisions: false,
            };
        }

        const decisionsByInvoice = await openFrameDispositionDialog([preview], {
            title: 'Void Invoice: Frame Handling',
            confirmLabel: 'Apply & Continue',
        });

        if (!decisionsByInvoice) {
            return null;
        }

        const invoiceDecisions = decisionsByInvoice[preview.invoiceId] || [];
        return {
            decisions: invoiceDecisions,
            requireVoidDispositionDecisions: true,
        };
    }, [openFrameDispositionDialog, state.id]);

    const startVoidWorkflow = useCallback(async (nextStatus) => {

        // for manual optical mode we don't need to collect frame disposition decisions, so we can skip that step, 
        // we can check that through preferences, if manual optical mode is enabled, we skip the frame disposition step
        if (preferences?.opticalEntryMode === 'manual') {
            setVoidWorkflow({
                inProgress: false,
                completed: true,
                decisions: [],
                requireVoidDispositionDecisions: false,
                stagedStatus: nextStatus,
                itemSignature: buildVoidItemSignature(state.invoiceItems),
            });
            return true;
        }

        const voidDisposition = await collectVoidDispositionDecisions();

        if (!voidDisposition) {
            return false;
        }

        setVoidWorkflow({
            inProgress: false,
            completed: true,
            decisions: voidDisposition.decisions,
            requireVoidDispositionDecisions: Boolean(voidDisposition.requireVoidDispositionDecisions),
            stagedStatus: nextStatus,
            itemSignature: buildVoidItemSignature(state.invoiceItems),
        });

        return true;
    }, [buildVoidItemSignature, collectVoidDispositionDecisions, state.invoiceItems]);

    const handleSave = async () => {
        if (wasClosedInitially) {
            toast.warn('This invoice is closed/refunded and cannot be modified.');
            return false;
        }
        if (!validate()) {
            console.log('Validation failed');
            return false;
        }

        // Clean up invoice items before sending to backend
        const processedInvoiceItems = state.invoiceItems.map(item => {
            const itemForSave = { ...item };
            
            // Clean up: Remove UI-only fields before sending to backend
            delete itemForSave.inventoryItem; // Large nested object, not needed by backend
            delete itemForSave.rx; // Only send rxId, not the full rx object
            delete itemForSave.frame; // Flat frame snapshot used only for dialog rehydration
            
            // Clean up itemConfig: keep IDs and snapshot data (names for display)
            if (itemForSave.itemConfig && itemForSave.type === ButtonType.LENS) {
                if (itemForSave.itemConfig?.manualLens) {
                    itemForSave.itemConfig = {
                        manualEntry: true,
                        manualLens: { ...itemForSave.itemConfig.manualLens },
                        model: itemForSave.itemConfig.model || null
                    };
                    return itemForSave;
                }

                const cleanConfig = {
                    // IDs for referential integrity
                    manufacturerId: itemForSave.itemConfig.manufacturerId,
                    lensModelId: itemForSave.itemConfig.lensModelId,
                    lensMaterialId: itemForSave.itemConfig.lensMaterialId,
                    lensIndexId: itemForSave.itemConfig.lensIndexId,
                    treatmentIds: itemForSave.itemConfig.treatmentIds || [],
                    
                    // Snapshot data for display (no DB lookups needed on read)
                    manufacturer: itemForSave.itemConfig.manufacturer ? {
                        id: itemForSave.itemConfig.manufacturer.id,
                        name: itemForSave.itemConfig.manufacturer.name
                    } : null,
                    model: itemForSave.itemConfig.model ? {
                        id: itemForSave.itemConfig.model.id,
                        modelName: itemForSave.itemConfig.model.modelName,
                        lensType: itemForSave.itemConfig.model.lensType
                    } : null,
                    material: itemForSave.itemConfig.material ? {
                        id: itemForSave.itemConfig.material.id,
                        name: itemForSave.itemConfig.material.name
                    } : null,
                    index: itemForSave.itemConfig.index ? {
                        id: itemForSave.itemConfig.index.id,
                        indexValue: itemForSave.itemConfig.index.indexValue
                    } : null,
                    treatments: (itemForSave.itemConfig.treatments || []).map(t => ({
                        id: t.id,
                        name: t.name,
                        cost: t.cost,
                        listPrice: t.listPrice
                    }))
                };
                itemForSave.itemConfig = cleanConfig;
            }
            
            return itemForSave;
        });

        // Gather all necessary data from state
        // Include ALL items (even those marked for deletion) - backend will handle deletions
        const invoiceDataToSave = {
            // If editing, include rowid
            id: state.id, // Pass the original ID if editing
            invoiceNumber: state.invoiceNumber,
            customerId: safeCustomer.id || null, // Assuming this is the customer ID
            associate: state.associate,
            creditMemos: state.creditMemos,
            tray: state.tray,
            invoiceItems: processedInvoiceItems, // Use cleaned items
            glassesOrdersForLab: state.glassesOrdersForLab || [],
            totalAmount: totalDue,
            paidAmount: totalCustomerPaid,
            balanceAmount: balanceDue,
            taxAmount: totalItemTaxAmount,
            discountAmount: totalOverallDiscountAmount,
            payments: state.payments, // Includes payments with _markedForDeletion flag
            discounts: state.discounts, // Includes discounts with _markedForDeletion flag
            claims: state.claims, // Includes claims with _markedForDeletion flag
            refunds: state.refunds, // Includes refunds with _markedForDeletion flag
            location: state.location,
            status: state.status, // Include the current status
            notes: state.notes, // Add notes if needed
            // Add Tray and Associate values (need to add state for these TextFields)
            // tray: state.tray,
            // associate: state.associate,
            invoiceDate: state.invoiceDate, // Or from state if added
            // Include formik values if they represent the core RX data for the order
            // rxData: formik.values,
            // Or if RX is an item, ensure item has rxDetails property
        };

        if (VOID_STATUSES.has(String(state.status || '').toLowerCase())) {
            // don't check if we are in manual mode
            if (preferences?.opticalEntryMode !== 'manual') {
                if (!voidWorkflow.completed || !voidWorkflow.stagedStatus || !VOID_STATUSES.has(String(voidWorkflow.stagedStatus || '').toLowerCase())) {
                    toast.error('Complete void steps first by selecting void status and confirming frame handling.');
                    return false;
                }

                const currentSignature = buildVoidItemSignature(state.invoiceItems);
                if (voidWorkflow.itemSignature !== currentSignature) {
                    setVoidWorkflow((prev) => ({
                        ...prev,
                        completed: false,
                        decisions: [],
                        requireVoidDispositionDecisions: false,
                        itemSignature: '',
                    }));
                    toast.error('Invoice items changed after void steps. Re-select void status to refresh disposition decisions.');
                    return false;
                }

                invoiceDataToSave.voidDispositionDecisions = voidWorkflow.decisions;
                invoiceDataToSave.requireVoidDispositionDecisions = Boolean(voidWorkflow.requireVoidDispositionDecisions);
            }
        }

        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.update(invoiceDataToSave);
            actions.setInvoiceMeta({ inProgress: false });
            const { code, message, data } = response;

            if (code !== 0) {
                console.error('Error saving invoice:', message);
                toast.error(message);
                return false;
            }
            // update invoice number in the state
            actions.setInvoiceMeta({
                invoiceNumber: data.invoiceNumber,
                id: data.id,
                status: data.status || state.status,
                dispensedDate: data.dispensedDate || null,
                dispensedBy: data.dispensedBy || ''
            });
            toast.success(message);

            // Refresh local edit state from the fully hydrated invoice payload.
            // The update endpoint response can be partial for nested lens/contact configs.
            await hydrateInvoiceById(data.id || state.id);

            // Refresh parent invoice list if callback provided
            if (onRefresh) {
                onRefresh();
            }

            if (VOID_STATUSES.has(String(data?.status || state.status || '').toLowerCase())) {
                resetVoidWorkflow();
            }

            return true;

        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error saving invoice:', error);
            }
            //toast.error(error.message ? error.message : 'Error saving invoice');
            return false;
        }
    }

    const handleDispenseInvoice = async () => {
        if (!canDispenseInvoice) {
            toast.error('Invoice is not ready to be dispensed');
            return;
        }

        const saved = await handleSave();
        if (!saved) {
            return;
        }

        try {
            actions.setInvoiceMeta({ inProgress: true });
            const response = await InvoiceService.dispenseInvoice(state.id);
            actions.setInvoiceMeta({ inProgress: false });

            const { code, message, data } = response;
            if (code !== 0) {
                toast.error(message);
                return;
            }

            actions.setInvoiceMeta({
                status: data.status || 'closed',
                dispensedDate: data.dispensedDate || new Date().toISOString(),
                dispensedBy: data.dispensedBy || state.dispensedBy || ''
            });

            toast.success(message);

            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            actions.setInvoiceMeta({ inProgress: false });
            if (isLocalDev) {
                console.error('Error dispensing invoice:', error);
            }
        }
    };

    const handleDispenseClick = () => {
        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                await handleDispenseInvoice();
            }
            closeConfirmationDialog();
        }, {
            title: 'Confirm Dispense',
            message: 'Dispensing will close this invoice and mark it as finalized. Continue?'
        });
    };

    const handleAssociateChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ associate: inputValue });
    }

    const handleTrayChange = (event) => {
        const inputValue = event.target.value;
        actions.setInvoiceMeta({ tray: inputValue });
    }

    const handleStatusChange = async (newStatus) => {
        const normalizedNewStatus = String(newStatus || '').toLowerCase();
        const previousStatus = state.status;
        const normalizedPreviousStatus = String(previousStatus || '').toLowerCase();

        if (!VOID_STATUSES.has(normalizedNewStatus)) {
            actions.setInvoiceMeta({ status: newStatus });
            if (VOID_STATUSES.has(normalizedPreviousStatus)) {
                resetVoidWorkflow();
            }
            return;
        }

        if (voidWorkflow.inProgress) {
            return;
        }

        if (voidWorkflow.completed && String(voidWorkflow.stagedStatus || '').toLowerCase() === normalizedNewStatus) {
            actions.setInvoiceMeta({ status: newStatus });
            return;
        }

        const warningMessage = balanceDue > 0
            ? `This invoice has an outstanding balance of $${balanceDue.toFixed(2)}. Voiding sets total and balance to $0.00 and may reverse frame/order handling. Continue?`
            : 'Voiding will finalize this invoice as void and may reverse frame/order handling. Continue?';

        const confirmed = await requestConfirmation({
            title: 'Confirm Void Invoice',
            message: warningMessage,
        });

        if (!confirmed) {
            actions.setInvoiceMeta({ status: previousStatus });
            resetVoidWorkflow();
            return;
        }

        setVoidWorkflow((prev) => ({
            ...prev,
            inProgress: true,
            completed: false,
            decisions: [],
            requireVoidDispositionDecisions: false,
            stagedStatus: null,
            itemSignature: '',
        }));

        actions.setInvoiceMeta({ status: newStatus });

        const staged = await startVoidWorkflow(newStatus);
        if (!staged) {
            actions.setInvoiceMeta({ status: previousStatus });
            resetVoidWorkflow();
            return;
        }

        toast.info('Void steps completed. Click Save to apply void changes.');
    }

    useEffect(() => {
        if (!open) {
            resetVoidWorkflow();
        }
    }, [open, resetVoidWorkflow]);

    const handleButtonClick = (buttonType) => {
        switch (buttonType) {
            case ButtonType.NEW_GLASSES:
                actions.setInvoiceMeta({ selectGlasses: true });
                break;  
            case ButtonType.MANUAL_GLASSES:
                actions.setInvoiceMeta({ selectManualGlasses: true });
                break;
            case ButtonType.MANUAL_LENS:
                actions.setInvoiceMeta({ selectManualLens: true });
                break;
            case ButtonType.MANUAL_CONTACT_LENS:
                actions.setInvoiceMeta({ selectManualContactLens: true });
                break;
            case ButtonType.CONTACT_LENS:
                actions.setInvoiceMeta({ selectConfiguredContactLens: true });
                break;
            case ButtonType.FRAME:
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            // Note: ButtonType.LENS removed - lens configuration is now part of NEW_GLASSES workflow
            case ButtonType.SERVICES:
                actions.setInvoiceMeta({ selectService: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            case ButtonType.SEND_TO_LAB:
                actions.setInvoiceMeta({ labOrderDialog: true });
                break;
            default:
                break;
        }
    }

    const handleEditItem = (item) => {
        const isPersistedManualEntry = item?.entryMethod === 'manual';
        const isManualFrame = item?.type === ButtonType.FRAME
            && isPersistedManualEntry;
        const isManualLens = item?.type === ButtonType.LENS
            && isPersistedManualEntry;
        const isManualContactLens = item?.type === ButtonType.CONTACT_LENS
            && isPersistedManualEntry;

        if (isManualFrame) {
            actions.setInvoiceMeta({
                editManualGlasses: true,
                manualFrameToEdit: {
                    item,
                    groupId: item.groupId || null,
                },
            });
            return;
        }

        if (isManualLens) {
            const groupId = item.groupId || null;
            const grouped = groupId
                ? state.invoiceItems.filter((invoiceItem) => invoiceItem.groupId === groupId)
                : [item];
            const odLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.LENS && invoiceItem.eye === 'OD') || null;
            const osLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.LENS && invoiceItem.eye === 'OS') || null;

            actions.setInvoiceMeta({
                editManualLens: true,
                manualLensToEdit: {
                    groupId,
                    odLens,
                    osLens,
                }
            });
            return;
        }

        if (isManualContactLens) {
            const groupId = item.groupId || null;
            const grouped = groupId
                ? state.invoiceItems.filter((invoiceItem) => invoiceItem.groupId === groupId)
                : [item];
            const odContactLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.CONTACT_LENS && invoiceItem.eye === 'OD') || null;
            const osContactLens = grouped.find((invoiceItem) => invoiceItem.type === ButtonType.CONTACT_LENS && invoiceItem.eye === 'OS') || null;

            actions.setInvoiceMeta({
                editManualContactLens: true,
                manualContactLensToEdit: {
                    groupId,
                    odContactLens,
                    osContactLens,
                }
            });
            return;
        }

        // show the editglasses dialog only if item type is glasses
        if (item.groupId && (item.type === ButtonType.FRAME || item.type === ButtonType.LENS)) {
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];

            const glassesData = mapGroupItemsToGlassesData(groupItems, item.groupId);

            actions.setInvoiceMeta({ editGlasses: true, glassesToEdit: glassesData });
        }
        // show the editcontacts dialog only if item type is contacts
        else if (item.type === ButtonType.CONTACT_LENS) {
            
            // Find all items with the same groupId
            const groupItems = item.groupId
                ? state.invoiceItems.filter(i => i.groupId === item.groupId)
                : [item];
            const OD = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OD');
            const OS = groupItems.find(i => i.type === 'contact_lens' && i.eye === 'OS');
            
            actions.setInvoiceMeta({ editContacts: true, contactsToEdit: { 
                items: groupItems, 
                OD, 
                OS, 
                rx: item.rx,
                groupId: item.groupId } });
        } else if (item.type === ButtonType.FRAME) {
            // Direct frame item edit - open frame dialog for replacement
            actions.setInvoiceMeta({ editFrame: true, editingFrameItem: item });
        } 
    };

    const handleDeleteItem = (item) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // For new items (with tempId), just remove from state
                if (item.tempId && !item.id) {
                    actions.removeItem(item);
                } else {
                    // For existing items, mark for deletion
                    actions.markItemForDeletion(item.id);
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark item for deletion? It will be removed when you save the invoice.`,
        });
    };

    const handleReplaceFrame = (newFrame) => {
        // Replace the existing frame item with the newly selected frame
        if (state.editingFrameItem) {
            const updatedFrameItem = {
                ...state.editingFrameItem,
                itemId: newFrame.id,
                modelNumber: newFrame.modelNumber,
                brand: newFrame.brand || null,
                description: newFrame.name || newFrame.modelNumber || newFrame.description || 'Frame',
                price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                tax: newFrame.defaultTaxPercentage || 0,
                total: calculateItemTotal({
                    price: +newFrame.listPrice || +newFrame.price || +newFrame.cost || 0,
                    quantity: state.editingFrameItem.quantity || 1,
                    tax: newFrame.defaultTaxPercentage || 0,
                    discount: state.editingFrameItem.discount || 0
                }),
                properties: newFrame.properties || {}
            };
            actions.updateItem({ item: state.editingFrameItem, updatedItem: updatedFrameItem });
            actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null });
        }
    };

    // Add this handler at the component level
    const handleItemUpdate = (item, field, value) => {
        actions.updateItem({ item, field, value });
    };

    /*
        called after glass order is edited and closed
    */
    const handleUpdateGlasses = (updatedGlasses) => {
        const glassesToEdit = state.glassesToEdit;
        if (glassesToEdit && glassesToEdit.items) {
            const nextItems = buildUpdatedGlassesInvoiceItems(glassesToEdit.items, updatedGlasses);

            if (nextItems.frameItem) {
                const currentFrameItem = glassesToEdit.items.find(item => item.type === 'frame' && item.groupId === updatedGlasses.groupId);
                actions.updateItem({ item: currentFrameItem, updatedItem: nextItems.frameItem });
            }

            if (nextItems.odLensItem) {
                const currentODLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OD' && item.groupId === updatedGlasses.groupId);
                actions.updateItem({ item: currentODLensItem, updatedItem: nextItems.odLensItem });
            }

            if (nextItems.osLensItem) {
                const currentOSLensItem = glassesToEdit.items.find(item => item.type === 'lens' && item.eye === 'OS' && item.groupId === updatedGlasses.groupId);
                actions.updateItem({ item: currentOSLensItem, updatedItem: nextItems.osLensItem });
            }

            const existingTreatments = glassesToEdit.items.filter(item => 
                item.type === ButtonType.TREATMENTS && item.groupId === updatedGlasses.groupId
            );
            existingTreatments.forEach(treatment => {
                actions.setInvoiceMeta({ type: 'REMOVE_ITEM', item: treatment });
            });
            nextItems.treatmentItems.forEach((treatmentItem) => {
                actions.addItem(treatmentItem);
            });

            actions.setInvoiceMeta({ editGlasses: false, glassesToEdit: null });
        }
    };

    /*
        called after contacts order is edited and closed
    */
    const handleUpdateContacts = (updatedContacts) => {
        const contactsToEdit = state.contactsToEdit;
        if (contactsToEdit) {
            // Extract OD and OS data from updatedContacts.contactLens
            const od = updatedContacts.od;
            const os = updatedContacts.os;
            const topLevelRx = updatedContacts.rx;

            // Update OD contact lens item
            const odContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OD' && item.groupId === updatedContacts.groupId);
            
            if (od) {
                const updatedODItem = {
                    ...odContactItem,
                    itemId: null,
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    isTrial: od.isTrial || false,
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OD',
                    total: calculateItemTotal({
                        ...odContactItem,
                        price: od.retailPrice || 0,
                        quantity: od.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: odContactItem, updatedItem: updatedODItem });
            }

            // Update OS contact lens item
            const osContactItem = contactsToEdit.items.find(item => item.type === ButtonType.CONTACT_LENS && item.eye === 'OS' && item.groupId === updatedContacts.groupId);
            if (os) {
                const updatedOSItem = {
                    ...osContactItem,
                    itemId: null,
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    isTrial: os.isTrial || false,
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox                        
                    },
                    rx: topLevelRx,
                    eye: 'OS',
                    total: calculateItemTotal({
                        ...osContactItem,
                        price: os.retailPrice || 0,
                        quantity: os.boxQuantity || 1
                    })
                };
                actions.updateItem({ item: osContactItem, updatedItem: updatedOSItem });
            }

            // No treatments for contacts, but if you ever add, handle here

            actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null });
        }
    }

    const handleUpdateManualFrame = (updatedManualGlasses) => {
        const editingItem = state.manualFrameToEdit?.item;
        if (!editingItem) {
            return;
        }

        const updatedFrameItem = buildGlassesInvoiceItems(updatedManualGlasses).find(
            (invoiceItem) => invoiceItem.type === ButtonType.FRAME
        );

        if (!updatedFrameItem) {
            actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null });
            return;
        }

        actions.updateItem({
            item: editingItem,
            updatedItem: {
                ...editingItem,
                ...updatedFrameItem,
                id: editingItem.id,
                groupId: editingItem.groupId,
            }
        });

        actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null });
    };

    const handleUpdateManualLens = (updatedManualLens) => {
        const { odLens, osLens } = updatedManualLens || {};
        const currentOD = state.manualLensToEdit?.odLens || null;
        const currentOS = state.manualLensToEdit?.osLens || null;

        if (currentOD) {
            if (odLens) {
                actions.updateItem({
                    item: currentOD,
                    updatedItem: {
                        ...currentOD,
                        ...odLens,
                        id: currentOD.id,
                    }
                });
            } else {
                actions.removeItem(currentOD);
            }
        } else if (odLens) {
            actions.addItem(odLens);
        }

        if (currentOS) {
            if (osLens) {
                actions.updateItem({
                    item: currentOS,
                    updatedItem: {
                        ...currentOS,
                        ...osLens,
                        id: currentOS.id,
                    }
                });
            } else {
                actions.removeItem(currentOS);
            }
        } else if (osLens) {
            actions.addItem(osLens);
        }

        actions.setInvoiceMeta({ editManualLens: false, manualLensToEdit: null });
    };

    const handleUpdateManualContactLens = (updatedManualContactLens) => {
        const { odContactLens, osContactLens } = updatedManualContactLens || {};
        const currentOD = state.manualContactLensToEdit?.odContactLens || null;
        const currentOS = state.manualContactLensToEdit?.osContactLens || null;

        if (currentOD) {
            if (odContactLens) {
                actions.updateItem({
                    item: currentOD,
                    updatedItem: {
                        ...currentOD,
                        ...odContactLens,
                        id: currentOD.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOD);
            }
        } else if (odContactLens) {
            actions.addItem({ ...odContactLens, entryMethod: 'manual' });
        }

        if (currentOS) {
            if (osContactLens) {
                actions.updateItem({
                    item: currentOS,
                    updatedItem: {
                        ...currentOS,
                        ...osContactLens,
                        id: currentOS.id,
                        entryMethod: 'manual',
                    }
                });
            } else {
                actions.removeItem(currentOS);
            }
        } else if (osContactLens) {
            actions.addItem({ ...osContactLens, entryMethod: 'manual' });
        }

        actions.setInvoiceMeta({ editManualContactLens: false, manualContactLensToEdit: null });
    };

    const addToInvoice = (type, item) => {

        // handle glasses item differently
        if (type === ButtonType.NEW_GLASSES) {
            buildGlassesInvoiceItems(item).forEach((invoiceItem) => {
                actions.addItem(invoiceItem);
            });

            // Store glasses order for backend processing
            actions.setInvoiceMeta({
                //glassesOrdersForLab: [...(state.glassesOrdersForLab || []), glassesOrder],
                selectService: false,
                selectTreatment: false,
                selectAccessory: false,
                selectGlasses: false,
                selectManualGlasses: false,
                selectManualLens: false,
                selectManualContactLens: false
            });
        } else if (type === ButtonType.MANUAL_LENS) {
            if (item.odLens) {
                actions.addItem(item.odLens);
            }
            if (item.osLens) {
                actions.addItem(item.osLens);
            }
            actions.setInvoiceMeta({ selectManualLens: false });
        } else if (type === ButtonType.MANUAL_CONTACT_LENS) {
            if (item.odContactLens) {
                actions.addItem({ ...item.odContactLens, entryMethod: 'manual' });
            }
            if (item.osContactLens) {
                actions.addItem({ ...item.osContactLens, entryMethod: 'manual' });
            }
            actions.setInvoiceMeta({ selectManualContactLens: false });
        } else if (type === ButtonType.CONTACT_LENS) {
           const { od, os, groupId, rx } = item;
            
            // Add Right Eye (OD) configured contact lens if it exists
            if (od && od.contactLensModelId) {
                const odItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OD): ${od.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    quantity: od.boxQuantity || 1,
                    price: od.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OD',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: od.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: od.manufacturerId,
                        contactLensModelId: od.contactLensModelId,
                        scheduleId: od.scheduleId,
                        boxQuantity: od.boxQuantity,
                        cost: od.cost,
                        retailPrice: od.retailPrice,
                        notes: od.notes,
                        modelName: od.modelName,
                        scheduleName: od.scheduleName,
                        daysSupply: od.daysSupply,
                        lensesPerBox: od.lensesPerBox
                    }
                };
                actions.addItem(odItem);
            }

            // Add Left Eye (OS) configured contact lens if it exists
            if (os && os.contactLensModelId) {
                const osItem = {
                    id: uuidv4(),
                    itemId: null, // No inventory item for configured contact lenses
                    description: `Contact Lens (OS): ${os.modelName || 'Contact Lens'}`,
                    type: ButtonType.CONTACT_LENS,
                    quantity: os.boxQuantity || 1,
                    price: os.retailPrice || 0,
                    tax: 0, // Tax calculated at invoice level
                    discount: 0,
                    total: 0,
                    date: getCurrentUTCDate(),
                    groupId: groupId,
                    eye: 'OS',
                    rxId: rx?.id || null, // Extract rxId from top-level rx (shared for both eyes)
                    rx: rx, // Store full rx object for editing before save                    
                    isTrial: os.isTrial || false,
                    // Store contact lens configuration metadata
                    itemConfig: {
                        manufacturerId: os.manufacturerId,
                        contactLensModelId: os.contactLensModelId,
                        scheduleId: os.scheduleId,
                        boxQuantity: os.boxQuantity,
                        cost: os.cost,
                        retailPrice: os.retailPrice,
                        notes: os.notes,
                        modelName: os.modelName,
                        scheduleName: os.scheduleName,
                        daysSupply: os.daysSupply,
                        lensesPerBox: os.lensesPerBox
                    }
                };
                actions.addItem(osItem);
            }

            // Close the dialog after adding items
            actions.setInvoiceMeta({
                selectConfiguredContactLens: false
            });
        } else if (type === ButtonType.FRAME) {
            // Add frame to invoice
            const frameItem = {
                id: uuidv4(), // Generate a unique ID for this instance in the UI state
                itemId: item.itemId || null, // Inventory item ID (null for patient own frames)
                modelNumber: item.modelNumber || null,
                brand: item.brand || null,
                description:  item.modelNumber,
                type: ButtonType.FRAME,
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
                groupId: null,
                frameHandling: 'stock',
                properties:  null
            };
            actions.addItem(frameItem);
        } else {
            // take description from name or modelNumber
            const newItem = {
                // Assign a unique ID for this instance in the UI. Generate a temporary UUID.            
                id: uuidv4(),
                itemId: item.id,
                description: item.name || item.modelNumber || item.description || 'New Item', // Use description from item if available
                type: type,
                quantity: item.quantity || 1,
                price: +item.listPrice || +item.price || +item.cost || +item.total || 0,
                tax: item.defaultTaxPercentage || 0,
                discount: item.discount || 0,
                total: 0,
                date: getCurrentUTCDate(),
            };

            // Dispatch an action to ADD_ITEM
            actions.addItem(newItem );
            actions.setInvoiceMeta({ selectService: false, selectTreatment: false, selectAccessory: false, selectGlasses: false });
        }
    }

    const handleTypeClick = (type) => {
        switch (type) {
            case 'frame':
                actions.setInvoiceMeta({ selectFrame: true });
                break;
            case ButtonType.TREATMENTS:
                actions.setInvoiceMeta({ selectTreatment: true });
                break;
            case ButtonType.ACCESSORIES:
                actions.setInvoiceMeta({ selectAccessory: true });
                break;
            case 'SEND_TO_LAB':
                actions.setInvoiceMeta({ labOrderDialog: true });
                break;
            default:
                break;
        }
    };

    const deletePayment = (paymentId) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // Always use markPaymentForDeletion - it will handle both temp and persisted payments
                actions.markPaymentForDeletion(paymentId);
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark payment for deletion? It will be removed when you save the invoice.`,
        });
    }

    // Refund handlers
    const handleAddRefund = async (refundData) => {
        try {
            const tempId = Date.now();
            const isUnifiedPayload = refundData && refundData.refund && refundData.returnInvoice;

            const stagedRefund = isUnifiedPayload
                ? {
                    ...refundData.refund,
                    returnInvoice: refundData.returnInvoice,
                    tempId,
                    invoiceId: state.id,
                    status: 'pending',
                    _stagedForSave: true
                }
                : {
                    ...refundData,
                    tempId,
                    invoiceId: state.id,
                    status: refundData?.status || 'pending',
                    _stagedForSave: true
                };

            actions.addRefund(stagedRefund);
            toast.info('Refund and return invoice staged. Click Save Invoice to apply changes.');
            return true;
        } catch (error) {
            if (isLocalDev) {
                console.error('Error staging refund with return invoice:', error);
            }
            toast.error('Failed to stage refund and return invoice');
            return false;
        }
    };

    const handleEditRefund = (refundData) => {
        const existingRefund = state.refunds.find(r =>
            (refundData.id && r.id === refundData.id) ||
            (refundData.tempId && r.tempId === refundData.tempId)
        );

        const mergedRefund = {
            ...(existingRefund || {}),
            ...refundData,
            returnInvoice: refundData.returnInvoice || existingRefund?.returnInvoice || null,
            _stagedForSave: existingRefund?._stagedForSave || refundData?._stagedForSave || false
        };

        // Just update local state - backend call happens when user clicks Save on invoice
        actions.updateRefund(mergedRefund);
    };

    const handleUpdateRefundStatus = async (refundId, status) => {
        try {
            const response = await InvoiceService.updateRefundStatus(refundId, status);
            const { code, message } = response;

            if (code === 0) {
                actions.setInvoiceMeta({ type: 'UPDATE_REFUND_STATUS', refundId, status });
                toast.success(message);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error('Error updating refund status:', error);
            toast.error('Error updating refund status');
        }
    };

    const handleDeleteRefund = (refundId) => {
        showConfirmationDialog((action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                // Always use markRefundForDeletion - it handles both temp and persisted
                actions.markRefundForDeletion(refundId);
            }
            closeConfirmationDialog();
        }, {
            title: 'Mark for deletion',
            message: `Mark refund for deletion? It will be removed when you save the invoice.`,
        });
    };

    // Credit Memo handlers
    const handleAddCreditMemo = async (creditMemoData) => {
        try {
            // Validate there's money available to credit
            if (maxRefundAmount <= 0) {
                toast.error('No funds available to credit. Customer must have paid amount exceeding refunds.');
                return;
            }

            // Validate credit memo amount doesn't exceed available credit
            if (creditMemoData.amount > maxRefundAmount) {
                toast.error(`Credit memo amount ($${creditMemoData.amount.toFixed(2)}) exceeds maximum available credit ($${maxRefundAmount.toFixed(2)})`);
                return;
            }
            
            const tempId = Date.now();
            const tempCreditMemo = { 
                ...creditMemoData, 
                tempId, 
                status: 'approved',
                invoiceId: state.id 
            };
            
            // Use the proper action to add credit memo to state
            actions.addCreditMemo(tempCreditMemo);
        } catch (error) {
            console.error('Error creating credit memo:', error);
            toast.error('Error creating credit memo');
        }
    };

    const handleEditCreditMemo = async (creditMemoData) => {
        try {
            // Note: Credit memo editing may be limited based on status
            // Use the proper action to update credit memo in state
            actions.updateCreditMemo(creditMemoData);
        } catch (error) {
            console.error('Error updating credit memo:', error);
            toast.error('Error updating credit memo');
        }
    };

    const handleUpdateCreditMemoStatus = async (creditMemoId, status) => {
        try {
            const response = await CreditMemoService.updateCreditMemoStatus(creditMemoId, status);
            const { code, message } = response;

            if (code === 0) {
                actions.setInvoiceMeta({
                    type: 'UPDATE_CREDIT_MEMO_STATUS',
                    creditMemoId,
                    status
                });
                toast.success(message);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error('Error updating credit memo status:', error);
            toast.error('Error updating credit memo status');
        }
    };

    const handleCancelCreditMemo = async (creditMemoId, reason) => {

        showConfirmationDialog(async (action) => {
            if (action === validLookup.confirmationDialogAction.ok) {
                const creditMemo = state.creditMemos.find(c => c.id === creditMemoId || c.tempId === creditMemoId);
                
                // If it's a temp credit memo (not yet saved), just remove it from state
                if (creditMemo && creditMemo.tempId && !creditMemo.id) {
                    actions.markCreditMemoForDeletion(creditMemoId);
                    closeConfirmationDialog();
                    return;
                }
                
                // If it's a persisted credit memo, call backend to cancel
                const response = await CreditMemoService.cancelCreditMemo(creditMemoId, reason);
                const { code, message } = response;

                if (code === 0) {
                    // Backend deleted it, so remove from UI state immediately
                    actions.removeCreditMemo(creditMemoId);
                    toast.success(message);
                } else {
                    toast.error(message);
                }
            }
            closeConfirmationDialog();
        }, {
            title: 'Delete record',
            message: `Delete credit memo record? This action cannot be undone.`,
        });
    };

    return (
        
        <Dialog 
            onClose={onClose} 
            open={open} 
            fullScreen 
            maxWidth="xl" 
            fullWidth 
            disableScrollLock
            scroll="paper"
        >
            <AppDialogTitle onClose={onClose} title={`Edit ${state.status === 'quote' ? 'Quote' : 'Invoice'} - ${state.status.toUpperCase()}`} />

            <DialogContent
                sx={{
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    position: 'relative',
                    '&::-webkit-scrollbar': {
                        width: 8,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,.3)',
                        borderRadius: 4,
                    },
                }}
            >
                {isHydrating ? (
                    <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body1">Loading invoice details...</Typography>
                    </Box>
                ) : hydrationError ? (
                    <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="error" sx={{ maxWidth: 560, width: '100%' }}>{hydrationError}</Alert>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" onClick={() => hydrateInvoiceById(invoiceId)} disabled={!invoiceId}>Retry</Button>
                            <Button variant="contained" onClick={onClose}>Close</Button>
                        </Box>
                    </Box>
                ) : (
                <>
                {wasClosedInitially && (
                    <Box sx={{ p: 2, mb: 2, backgroundColor: 'warning.light', color: 'warning.contrastText', textAlign: 'center', borderRadius: 1 }}>
                        <Typography variant="h6">This invoice is Closed/Refunded and cannot be edited.</Typography>
                    </Box>
                )}

                <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                    <CustomerInfobar customerData={customerContext} />
                </Box>

                <Box display={'flex'} alignItems="center" justifyContent='space-between' sx={{ mb: 1, mt: 1 }}>
                    <Box>
                        <Typography sx={{ pr: 1 }}>Invoice date: {formatDateWithPreferences(state.invoiceDate, preferences)}</Typography>
                        {
                            state.invoiceNumber && (<Typography sx={{ pr: 2, fontWeight: 'bold' }}>Invoice #: {state.invoiceNumber}</Typography>)
                        }
                        {state.dispensedDate && (
                            <Typography sx={{ pr: 2 }}>
                                Dispensed: {formatDateWithPreferences(state.dispensedDate, preferences)}{state.dispensedBy ? ` by ${state.dispensedBy}` : ''}
                            </Typography>
                        )}
                    </Box>

                    <Box display={'flex'} alignItems="center">
                        <TextField label="Tray #" labelid="tray" size="small" sx={{ pr: 1 }} value={state.tray}
                            onChange={handleTrayChange} error={!!state.errors.tray}
                            helperText={state.errors.tray} disabled={wasClosedInitially}></TextField>
                        <TextField label="Associate" labelid="associate" size="small" sx={{ pr: 1 }}
                            value={state.associate} error={!!state.errors.associate}
                            helperText={state.errors.associate} onChange={handleAssociateChange} disabled={wasClosedInitially}></TextField>
                        <SelectLocation location={state.location ? +state.location.id : ''} setLocation={(location) => actions.setInvoiceMeta({ location: location })} error={!!state.errors.location} helperText={state.errors.location} disabled={wasClosedInitially} />
                    </Box>
                </Box>

                <InvoiceButtonComponent
                    onButtonClick={handleButtonClick}
                    disabled={wasClosedInitially}
                />
                <Divider sx={{ mt: 1 }} />

                <InvoiceItemsTable
                    invoiceItems={state.invoiceItems}
                    onEditItem={handleEditItem}
                    onDeleteItem={handleDeleteItem}
                    onItemUpdate={handleItemUpdate}
                    disabled={wasClosedInitially}
                />

                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={6}>
                        <DiscountPromotion
                            discounts={state.discounts}
                            orderSubtotal={lineItemsSubtotal}
                            onAddDiscount={(discount) => actions.addDiscount(discount)}
                            onEditDiscount={(discount) => actions.updateDiscount(discount)}
                            onDeleteDiscount={(discountId) => {
                                showConfirmationDialog((action) => {
                                    if (action === validLookup.confirmationDialogAction.ok) {
                                        actions.markDiscountForDeletion(discountId);
                                    }
                                    closeConfirmationDialog();
                                }, {
                                    title: 'Mark for deletion',
                                    message: `Mark discount for deletion? It will be removed when you save the invoice.`,
                                });
                            }}
                            disabled={wasClosedInitially}
                        />
                        {/* Insurance Claims Component */}
                        <InsuranceClaimCollector
                            claims={state.claims}
                            customer={customerContext}
                            amountToClaim={totalDue}
                            onAddClaim={(claim) => actions.addClaim( claim )}
                            onEditClaim={(claim) => actions.updateClaim( claim )}
                            onDeleteClaim={(claimId) => {
                                showConfirmationDialog((action) => {
                                    if (action === validLookup.confirmationDialogAction.ok) {
                                        actions.markClaimForDeletion(claimId);
                                    }
                                    closeConfirmationDialog();
                                }, {
                                    title: 'Mark for deletion',
                                    message: `Mark insurance claim for deletion? It will be removed when you save the invoice.`,
                                });
                            }}
                            disabled={wasClosedInitially || isQuote}
                        />
                        {/* Payment Collector Component */}
                        <PaymentCollector
                            payments={state.payments}
                            onAddPayment={(payment) => actions.addPayment( payment )}
                            onEditPayment={(payment) => actions.updatePayment( payment )}
                            onDeletePayment={deletePayment}
                            disabled={wasClosedInitially || isQuote}
                            customerId={safeCustomer.id || null}
                            invoiceId={state.id}
                            invoiceBalance={balanceDue}
                            onRefresh={onRefresh}
                        />
                        {/* Credit Memo Collector Component */}
                        <CreditMemoCollector
                            creditMemos={state.creditMemos}
                            onAddCreditMemo={handleAddCreditMemo}
                            onEditCreditMemo={handleEditCreditMemo}
                            onCancelCreditMemo={handleCancelCreditMemo}
                            onUpdateCreditMemoStatus={handleUpdateCreditMemoStatus}
                            disabled={wasClosedInitially || isQuote}
                            maxCreditAmount={maxRefundAmount}
                            invoiceData={hydratedInvoiceData}
                        />
                        {/* Refund Collector Component */}
                        <RefundCollector
                            refunds={state.refunds}
                            onAddRefund={handleAddRefund}
                            onEditRefund={handleEditRefund}
                            onDeleteRefund={handleDeleteRefund}
                            onUpdateRefundStatus={handleUpdateRefundStatus}
                            disabled={wasClosedInitially || isQuote}
                            payments={state.payments}
                            maxRefundAmount={maxRefundAmount}
                            invoiceItems={state.invoiceItems}
                            claims={state.claims}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} mt={1}>
                        <TextField
                            value={state.notes}
                            onChange={(e) => actions.setInvoiceMeta({ notes: e.target.value })}
                            name="notes"
                            label="Notes"
                            multiline
                            rows={4}
                            fullWidth
                            disabled={wasClosedInitially}
                        />
                        <Box sx={{ mt: 1, p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                            <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Subtotal:</Typography>
                                <Typography>{formatAmountForDisplayWithPreferences(lineItemsSubtotal, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Discount:</Typography>
                                <Typography>{hasNonZeroDiscount ? '-' : ''}{formatAmountForDisplayWithPreferences(totalDiscount, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Tax:</Typography>
                                <Typography>{formatAmountForDisplayWithPreferences(taxOnDiscountedAmount, preferences)}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6">Total Due:</Typography>
                                <Typography variant="h6">{formatAmountForDisplayWithPreferences(totalDue, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography>Total Customer Paid:</Typography>
                                    <Typography variant="caption">Or Credit memo:</Typography>
                                </Box>
                                <Typography>{formatAmountForDisplayWithPreferences(totalCustomerPaid, preferences)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Insurance Approved:</Typography> {/* New label */}
                                <Typography>{formatAmountForDisplayWithPreferences(totalInsuranceApproved, preferences)}</Typography>
                            </Box>
                            {/* we should not show refund on source or original invoice */}
                            {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography>Total Refunded:</Typography>
                                <Typography sx={{ color: 'error.main' }}>{hasNonZeroRefunded ? '-' : ''}{formatAmountForDisplayWithPreferences(totalRefunded, preferences)}</Typography>
                            </Box> */}
                            {/* {totalStoreCredit > 0 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography>Available Store Credit:</Typography>
                                    <Typography sx={{ color: 'primary.main' }}>{formatAmountForDisplayWithPreferences(totalStoreCredit, preferences)}</Typography>
                                </Box>
                            )} */}
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Balance:</Typography>
                                {/* Highlight Balance */}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: balanceDue > 0 ? 'error.main' : (balanceDue < 0 ? 'success.main' : 'text.primary')
                                    }}
                                >
                                    {formatAmountForDisplayWithPreferences(balanceDue, preferences)}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                </>
                )}
            </DialogContent >
            <DialogActions sx={{ p: 2, boxShadow: 8 }}>
                {
                    (state.errors.tray || state.errors.location || state.errors.associate) && <Typography color="error" sx={{ pr: 1 }}>Please fix issues before submitting</Typography>
                }
                {
                    state.inProgress ? <Typography>Saving...</Typography> :
                        (
                            <>
                                {/* Status Dropdown */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <InvoiceStatusSelect statusTypes={invoiceStatusType} isLoading={invoivceStatusTypeLoading} selectedStatus={state.status} onStatusChange={handleStatusChange} disabled={wasClosedInitially || voidWorkflow.inProgress} excludedStatuses={['refund']} />

                                    {VOID_STATUSES.has(String(state.status || '').toLowerCase()) && voidWorkflow.completed && (
                                        <Chip
                                            label="Void steps complete"
                                            color="success"
                                            size="small"
                                            sx={{ fontSize: '0.75rem' }}
                                        />
                                    )}

                                    {isQuote && (
                                        <Box onClick={handleTypeClick} sx={{ cursor: 'pointer' }}>
                                            <Chip
                                                icon={<InfoIcon />}
                                                label="Quote Mode"
                                                color="info"
                                                size="small"
                                                sx={{ fontSize: '0.75rem' }}
                                            />
                                        </Box>
                                    )}
                                </Box>

                                {state.invoiceItems.length > 0 && (
                                    <>
                                        {hasInvoiceLifecycleActions && (
                                            <>
                                                {linkedReturnInvoice && String(state.status || '').toLowerCase() !== 'refund' && onOpenReturnInvoice && (
                                                    <Button
                                                        color="danger"
                                                        variant="outlined"
                                                        onClick={handleOpenLinkedReturnInvoice}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        Open Return Invoice {linkedReturnInvoice.invoiceNumber}
                                                    </Button>
                                                )}
                                                <Button
                                                    color="warning"
                                                    variant="outlined"
                                                    endIcon={<ArrowDropDownIcon />}
                                                    onClick={handleOpenInvoiceActionsMenu}
                                                    disabled={!hasInvoiceLifecycleActions}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Invoice Actions
                                                </Button>
                                                <Menu
                                                    anchorEl={invoiceActionsMenuAnchorEl}
                                                    open={Boolean(invoiceActionsMenuAnchorEl)}
                                                    onClose={handleCloseInvoiceActionsMenu}
                                                >
                                                    {isQuote && (
                                                        <MenuItem
                                                            onClick={async () => {
                                                                handleCloseInvoiceActionsMenu();
                                                                await handleConvertToInvoice();
                                                            }}
                                                            disabled={wasClosedInitially || state.inProgress}
                                                        >
                                                            Convert to Invoice
                                                        </MenuItem>
                                                    )}
                                                    {preferences.allowInvoiceTransfer && (
                                                        <Can section='invoice' action='transfer-invoice'>
                                                        <MenuItem
                                                            onClick={() => {
                                                                handleCloseInvoiceActionsMenu();
                                                                handleTransferInvoice();
                                                            }}
                                                            disabled={!canTransferInvoice}
                                                        >
                                                            Transfer Invoice
                                                        </MenuItem>
                                                        </Can>
                                                    )}
                                                    <Can section='invoice' action='edit-invoice'>
                                                        <MenuItem
                                                            onClick={() => {
                                                                handleCloseInvoiceActionsMenu();
                                                                handleDispenseClick();
                                                            }}
                                                            disabled={!canDispenseInvoice}
                                                        >
                                                            Dispense Invoice
                                                        </MenuItem>
                                                    </Can>
                                                </Menu>
                                            </>
                                        )}
                                        {hasMessagingOptions && (
                                            <>
                                                <Button
                                                    color="info"
                                                    variant="outlined"
                                                    endIcon={<ArrowDropDownIcon />}
                                                    onClick={handleOpenMessagingMenu}
                                                    disabled={!canSendTextNow && !canSendEmailNow}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Messaging
                                                </Button>
                                                <Menu
                                                    anchorEl={messagingMenuAnchorEl}
                                                    open={Boolean(messagingMenuAnchorEl)}
                                                    onClose={handleCloseMessagingMenu}
                                                >
                                                    {canSendText && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                handleCloseMessagingMenu();
                                                                handleSendTextNotification();
                                                            }}
                                                            disabled={!canSendTextNow}
                                                        >
                                                            Send Pickup Text
                                                        </MenuItem>
                                                    )}
                                                    {canSendEmail && (
                                                        <MenuItem
                                                            onClick={() => {
                                                                handleCloseMessagingMenu();
                                                                handleSendEmailNotification();
                                                            }}
                                                            disabled={!canSendEmailNow}
                                                        >
                                                            Send Email
                                                        </MenuItem>
                                                    )}
                                                </Menu>
                                            </>
                                        )}
                                        {/* <Button
                                            onClick={handleTestPrintInvoice}
                                            color="warning"
                                            variant="outlined"
                                            startIcon={<PrintIcon />}
                                            sx={{ mr: 1 }}
                                        >
                                            Test Print (Simple HTML)
                                        </Button> */}
                                        <Can section='invoice' action='print-invoice'>
                                            <Button
                                                color="secondary"
                                                variant="outlined"
                                                startIcon={<PrintIcon />}
                                                endIcon={<ArrowDropDownIcon />}
                                                onClick={(e) => setPrintMenuAnchorEl(e.currentTarget)}
                                                disabled={!state.invoiceNumber || isPrinting}
                                                sx={{ mr: 1 }}
                                            >
                                                Print
                                            </Button>
                                            <Menu
                                                anchorEl={printMenuAnchorEl}
                                                open={Boolean(printMenuAnchorEl)}
                                                onClose={() => setPrintMenuAnchorEl(null)}
                                            >
                                                <MenuItem disableRipple sx={{ p: 0 }}>
                                                    <TemplatePrintButton
                                                        ref={invoicePrintRef}
                                                        documentType={state.status === 'quote' ? 'quote' : 'invoice'}
                                                        onPrint={async (tId) => { setPrintMenuAnchorEl(null); await handlePrintInvoice(tId); }}
                                                        disabled={!state.invoiceNumber || isPrinting}
                                                        buttonProps={{ variant: 'text', color: 'inherit', fullWidth: true, sx: { justifyContent: 'flex-start', px: 2, py: 1 } }}
                                                    >
                                                        {state.status === 'quote' ? 'Quote' : 'Customer Invoice'}
                                                    </TemplatePrintButton>
                                                </MenuItem>
                                                {state.claims?.length > 0 && (
                                                    <MenuItem disableRipple sx={{ p: 0 }}>
                                                        <TemplatePrintButton
                                                            ref={superbillPrintRef}
                                                            documentType="insurance_invoice"
                                                            onPrint={async (tId) => { setPrintMenuAnchorEl(null); await handlePrintSuperbill(tId); }}
                                                            disabled={!state.invoiceNumber || isPrinting}
                                                            buttonProps={{ variant: 'text', color: 'inherit', fullWidth: true, sx: { justifyContent: 'flex-start', px: 2, py: 1 } }}
                                                        >
                                                            Insurance Invoice (Superbill)
                                                        </TemplatePrintButton>
                                                    </MenuItem>
                                                )}
                                                {balanceDue === 0 && state.invoiceNumber && state.status !== 'quote' && (
                                                    <MenuItem disableRipple sx={{ p: 0 }}>
                                                        <TemplatePrintButton
                                                            ref={receiptPrintRef}
                                                            documentType="receipt"
                                                            onPrint={async (tId) => { setPrintMenuAnchorEl(null); await handlePrintReceipt(tId); }}
                                                            disabled={!state.invoiceNumber || isPrinting}
                                                            buttonProps={{ variant: 'text', color: 'inherit', fullWidth: true, sx: { justifyContent: 'flex-start', px: 2, py: 1 } }}
                                                        >
                                                            Receipt
                                                        </TemplatePrintButton>
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        </Can>

                                        <Button
                                            onClick={handleAddTask}
                                            color="info"
                                            variant="outlined"
                                            startIcon={<PlaylistAddCheckIcon />}
                                        >
                                            Add Task
                                        </Button> 
                                        <Can section='invoice' action='edit-invoice'>                                       
                                            <Button sx={{ ml: 2 }} color="primary" variant="contained" type="button" onClick={async () => { 
                                                if (!validate()) return
                                                const saved = await handleSave(); 
                                                if (saved) {
                                                    onClose();
                                                }
                                            }} autoFocus disabled={wasClosedInitially}>
                                                Save & Close
                                            </Button>
                                            <Button sx={{ ml: 1 }} color="primary" variant="outlined" type="button" onClick={handleSave} disabled={wasClosedInitially}>
                                                Save
                                            </Button>
                                        </Can>
                                    </>
                                )}
                                <Button color="cancel" variant="outlined" onClick={onClose}>Cancel</Button>
                            </>

                        )
                }
            </DialogActions>
            {/* Use NewGlassesDialog for both add and edit - it supports editing via existingGlassesData prop */}
            <NewGlassesDialog 
                open={state.editGlasses} 
                onClose={() => {
                    actions.setInvoiceMeta({ editGlasses: false, glassesToEdit: null });
                }} 
                onSelect={handleUpdateGlasses}
                customer={customerContext}
                existingGlassesData={state.glassesToEdit}
                location={state.location}
            />
            {/* <EditContactsDialog
                open={state.editContacts}
                onClose={() => {
                    actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null });
                }}
                contacts={state.contactsToEdit}
                onUpdate={handleUpdateContacts}
                onDeleteItem={handleDeleteItem}
                invoiceNumber={state.invoiceNumber}
                customer={customer}
            /> */}
            <NewGlassesDialog open={state.selectGlasses} 
            onClose={() => actions.setInvoiceMeta({ selectGlasses: false })} 
            onSelect={(glasses) => addToInvoice(ButtonType.NEW_GLASSES, glasses)} customer={customerContext} location={state.location} />
            <ManualGlassesDialog
                open={state.selectManualGlasses}
                onClose={() => actions.setInvoiceMeta({ selectManualGlasses: false })}
                onSelect={(manualFrameGlasses) => addToInvoice(ButtonType.NEW_GLASSES, manualFrameGlasses)}
                location={state.location}
            />
            <ManualLensDialog
                open={state.selectManualLens}
                onClose={() => actions.setInvoiceMeta({ selectManualLens: false })}
                onSelect={(manualLensData) => addToInvoice(ButtonType.MANUAL_LENS, manualLensData)}
            />
            <ManualContactLensDialog
                open={state.selectManualContactLens}
                onClose={() => actions.setInvoiceMeta({ selectManualContactLens: false })}
                onSelect={(manualContactLensData) => addToInvoice(ButtonType.MANUAL_CONTACT_LENS, manualContactLensData)}
            />
            <ManualGlassesDialog
                open={state.editManualGlasses}
                onClose={() => actions.setInvoiceMeta({ editManualGlasses: false, manualFrameToEdit: null })}
                onSelect={handleUpdateManualFrame}
                location={state.location}
                existingManualFrameData={state.manualFrameToEdit}
            />
            <ManualLensDialog
                open={state.editManualLens}
                onClose={() => actions.setInvoiceMeta({ editManualLens: false, manualLensToEdit: null })}
                onSelect={handleUpdateManualLens}
                existingManualLensData={state.manualLensToEdit}
            />
            <ManualContactLensDialog
                open={state.editManualContactLens}
                onClose={() => actions.setInvoiceMeta({ editManualContactLens: false, manualContactLensToEdit: null })}
                onSelect={handleUpdateManualContactLens}
                existingManualContactLensData={state.manualContactLensToEdit}
            />

            {/* For new contact lens */}
            <ContactLensConfigurationDialog 
                open={state.selectConfiguredContactLens} 
                onClose={() => actions.setInvoiceMeta({ selectConfiguredContactLens: false })} 
                customer={customerContext}
                onSave={(contactLensData) => {
                    // Add groupId to the data
                    const dataWithGroupId = {
                        ...contactLensData,
                        groupId: uuidv4()
                    };
                    addToInvoice(ButtonType.CONTACT_LENS, dataWithGroupId);
                }} 
            />            
             {/* For editing a contact lens */}
            <ContactLensConfigurationDialog 
                open={state.editContacts} 
                onClose={() => actions.setInvoiceMeta({ editContacts: false, contactsToEdit: null })} 
                customer={customerContext}
                onSave={handleUpdateContacts} 
                existingContactLensData={state.contactsToEdit}
            />
            <ServiceSelectDialog open={state.selectService} onClose={() => actions.setInvoiceMeta({ selectService: false })} onSelect={(service) => addToInvoice(ButtonType.SERVICES, service)} />
            {/* <LabOrderDialog
                open={state.labOrderDialog}
                onClose={() => actions.setInvoiceMeta({ labOrderDialog: false })}
                onSave={async (labOrderData) => {
                    try {
                        const labOrderToSave = {
                            ...labOrderData,
                            invoiceId: state.id,
                            groupId: uuidv4(),
                            orderType: 'glasses',
                        };

                        const response = await LabOrderService.saveLabOrder(labOrderToSave);
                        const { code, message } = response;

                        if (code === 0) {
                            toast.success(message || 'Lab order saved successfully');
                            actions.setInvoiceMeta({
                                labOrderDialog: false,
                                glassesOrdersForLab: [...state.glassesOrdersForLab, labOrderToSave]
                            });
                        } else {
                            toast.error(message || 'Failed to save lab order');
                        }
                    } catch (error) {
                        toast.error('Error saving lab order');
                        console.error('Lab order save error:', error);
                    }
                }}
            /> */}
            <TreatmentSelectDialog open={state.selectTreatment} onClose={() => actions.setInvoiceMeta({ selectTreatment: false })} onSelect={(treatment) => addToInvoice(ButtonType.TREATMENTS, treatment)} />
            <AccessorySelectDialog open={state.selectAccessory} onClose={() => actions.setInvoiceMeta({ selectAccessory: false })} onSelect={(accessory) => addToInvoice(ButtonType.ACCESSORIES, accessory)} />
            <FrameSelectDialog open={state.selectFrame} onClose={() => actions.setInvoiceMeta({ selectFrame: false })} onSelect={(frame) => addToInvoice(ButtonType.FRAME, frame)} />
            <FrameSelectDialog open={state.editFrame} onClose={() => actions.setInvoiceMeta({ editFrame: false, editingFrameItem: null })} onSelect={handleReplaceFrame} />                
            {/* LensSelectDialog removed - lens configuration is now part of NewGlassesDialog workflow */}

            <AddTaskDialog
                open={state.showTaskDialog}
                onClose={() => actions.setInvoiceMeta({ showTaskDialog: false })}
                entityType="invoice"
                entityId={state.id}
                onTaskCreated={() => actions.setInvoiceMeta({ showTaskDialog: false })}
            />

            <FrameDispositionDialog
                open={frameDispositionDialog.open}
                previews={frameDispositionDialog.previews}
                title={frameDispositionDialog.title}
                confirmLabel={frameDispositionDialog.confirmLabel}
                onClose={closeFrameDispositionDialog}
                onConfirm={confirmFrameDispositionDialog}
                inProgress={frameDispositionDialog.inProgress}
            />

            <TransferInvoiceDialog
                open={state.showTransferDialog}
                onClose={() => actions.setInvoiceMeta({ showTransferDialog: false })}
                invoiceData={hydratedInvoiceData}
                onTransferSuccess={handleTransferSuccess}
                onRefresh={onRefresh}
                hasPayments={state.payments && state.payments.length > 0} // Pass whether original invoice has payments
                hasDiscounts={state.discounts && state.discounts.length > 0} // Pass whether original invoice has discounts
                hasInsurance={state.claims && state.claims.length > 0} // Pass whether original invoice has insurance claims
            />

            <CreateReturnInvoiceDialog
                open={showCreateReturnInvoiceDialog}
                onClose={() => setShowCreateReturnInvoiceDialog(false)}
                onSave={handleCreateReturnInvoice}
                invoiceItems={state.invoiceItems}
                loading={state.inProgress}
            />

            <SendSmsDialog
                open={sendSmsDialog.open}
                recipient={sendSmsDialog.invoice ? {
                    recipientId: safeCustomer.id,
                    phone: safeCustomer.mobile || safeCustomer.phone,
                    name: safeCustomer.name || `${safeCustomer.firstName || ''} ${safeCustomer.lastName || ''}`.trim(),
                    email: safeCustomer.email
                } : null}
                entityId={sendSmsDialog.invoice?.id}
                entityType="invoice"
                defaultCommunicationType="order_ready"
                onClose={() => setSendSmsDialog({ open: false, invoice: null })}
                onSuccess={(message) => {
                    toast.success(message);
                    setSendSmsDialog({ open: false, invoice: null });
                }}
            />

            <SendInvoiceEmailDialog
                open={sendEmailDialog.open}
                recipient={sendEmailDialog.invoice ? {
                    recipientId: safeCustomer.id,
                    email: safeCustomer.email,
                    name: safeCustomer.name || `${safeCustomer.firstName || ''} ${safeCustomer.lastName || ''}`.trim()
                } : null}
                entityId={sendEmailDialog.invoice?.id}
                invoiceNumber={sendEmailDialog.invoice?.invoiceNumber}
                onClose={() => setSendEmailDialog({ open: false, invoice: null })}
                onSuccess={(message) => {
                    toast.success(message);
                    setSendEmailDialog({ open: false, invoice: null });
                }}
            />

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />

            <PrintPreviewDialog url={previewUrl} onClose={closePreview} />
        </Dialog >
    )
}